import { defineComponent as _defineComponent } from 'vue'
import {
  type ComputedRef,
  computed,
  nextTick,
  onMounted,
  ref,
  unref,
  useContext,
  watch,
} from "@nuxtjs/composition-api";

import { useVuetify } from "@/composables";
import { CREATOR_SHOP_PRODUCT_FEED_COLUMNS } from "@/utils/constants";
import { getProductTileDimensions } from "@/utils/services/ProductItemHeightCalculator";
import { shouldKeepRowInVirtualList } from "@/utils/virtualList";

type Item = { id: string; position: number };
type Row = { position: number; items: Array<Item>; offsetY: number };
type Grid = { rows: Array<Row>; totalHeight: number; totalRows: number };


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductFeed',
  props: {
  profileId: { type: String, required: true }, // needed to retrieve products
  productIds: { type: Array, required: true, default: () => [] },
  showLoading: { type: Boolean, required: true, default: false },
  showError: { type: Boolean, required: true, default: false },
  isVisible: { type: Boolean, default: false }, // used to force layout calculation(s)
  columns: { type: Object, default: () => CREATOR_SHOP_PRODUCT_FEED_COLUMNS }, // num of columns by breakpoint
  spacing: { type: Object, default: () => ({ gap: 8 }) }, // flex gap between rows & columns
  buffer: { type: Number, default: 3 }, // num of rows, from bottom, before a pagination occurs
  isSearch: { type: Boolean, default: false },
},
  emits: ["page"],
  setup(__props, { emit: emits }) {

const props = __props;





const iPhoneXWidth = 375;
const iPhoneXHeight = 812;
const resizeDelayOnTransition = 100;

const context = useContext();
const vuetify = useVuetify();

const containerRef = ref();
const containerOffset = ref(0); // value of offsetTop for .product-feed
const viewportHeight = ref(iPhoneXHeight); // what it says =P
const scrollPosition = ref(0); // equal to document.documentElement.scrollTop
const rowWidth = ref(0); // width of .product-feed__row
const rowHeight = ref(0); // height of .product-feed__row
const itemHeight = ref(0); // height of .product-feed__item
const itemWidth = ref(0); // width of .product-feed__item

const numColumns = computed(() => {
  if (process.server) {
    return context.isMobile ? props.columns.xs : context.isTablet ? props.columns.sm : props.columns.lg;
  }

  return vuetify.breakpoint.xl
    ? props.columns.xl
    : vuetify.breakpoint.lg
    ? props.columns.lg
    : vuetify.breakpoint.md
    ? props.columns.md
    : vuetify.breakpoint.sm
    ? props.columns.sm
    : props.columns.xs;
});

onMounted(() => {
  onResize();
  onScroll();
});

const visibleGridItems: ComputedRef<Array<Row>> = computed(() =>
  process.server
    ? []
    : gridItems.value.rows.filter((row: Row) =>
        shouldKeepRowInVirtualList({
          rowHeight: itemHeight.value,
          viewportHeight: viewportHeight.value,
          rowContainerOffsetTop: containerOffset.value,
          rowTopPosition: row.offsetY,
          scrollPosition: scrollPosition.value,
        }),
      ),
);

const gridItems: ComputedRef<Grid> = computed(() => {
  let batchIteration = 0;
  const data: Grid = { rows: [], totalHeight: 0, totalRows: 0 };

  if (process.server) {
    return data; // early return
  }

  // Batch products based on column count into a grid
  for (let x = 0; x < unref(props.productIds).length; x += numColumns.value) {
    const rowItems: Array<Item> = unref(props.productIds)
      .slice(x, x + numColumns.value)
      .map((id) => ({ id, position: x } as Item));

    data.rows.push({
      position: batchIteration,
      items: rowItems,
      offsetY: batchIteration !== 0 ? batchIteration * rowHeight.value : 0,
    });

    batchIteration += 1;
    data.totalRows += 1;
    data.totalHeight += rowHeight.value;
  }

  return data;
});

const onResize = () => {
  nextTick(() => {
    viewportHeight.value = window.innerHeight || document.documentElement.clientHeight;
    containerOffset.value = containerRef.value?.offsetTop || 0;
    rowWidth.value = containerRef.value?.clientWidth || iPhoneXWidth;

    const [height, width] = getProductTileDimensions({
      width: rowWidth.value,
      columns: numColumns.value,
      gap: props.spacing.gap,
    });

    itemHeight.value = height;
    itemWidth.value = width;
    rowHeight.value = height + props.spacing.gap;
  });
};

const onScroll = () => {
  scrollPosition.value = document.documentElement.scrollTop;
};

const onPaginate = () => emits("page");

watch(() => vuetify.breakpoint.name, onResize);

watch(
  // This is necessary as the <c-tab-item /> is programmatically set to `display: none` by Vuetify.
  // Without this the onResize() incorrectly calculates as this child element is not "visible".
  () => props.isVisible,
  (newVal, prevVal) => {
    if (prevVal === false && newVal === true) {
      setTimeout(onResize, resizeDelayOnTransition);
    }
  },
);

watch(
  // This is necessary for when search results, or clearing a search result, replaces the amount of returned
  // ids from the parent component. It prevents a bug with the virtual list not realizing that the users scroll
  // position has updated programmatically via a ProductFeedSearch component firing `window.scrollTo()`
  () => gridItems.value.totalHeight,
  (newHeight, prevHeight) => newHeight < prevHeight && onScroll(),
);

return { __sfc: true,props, emits, iPhoneXWidth, iPhoneXHeight, resizeDelayOnTransition, context, vuetify, containerRef, containerOffset, viewportHeight, scrollPosition, rowWidth, rowHeight, itemHeight, itemWidth, numColumns, visibleGridItems, gridItems, onResize, onScroll, onPaginate }
}

})